
import { useState, useEffect } from "react";
import { toast } from "sonner";
import type { AnalyticsConfig } from "../components/AnalyticsSettings";

const SETTINGS_STORAGE_KEY = "analytics-settings";

const DEFAULT_SETTINGS: AnalyticsConfig = {
  showKPIs: true,
  showSourceAnalytics: true,
  showTimeToHire: true,
  showRevenueAnalytics: true,
  showRecruiterPerformance: true,
  defaultDateRange: '6m',
  refreshInterval: 300000, // 5 minutes
  chartTheme: 'auto'
};

export const useAnalyticsSettings = () => {
  const [settings, setSettings] = useState<AnalyticsConfig>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load settings from localStorage
    try {
      const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
      if (stored) {
        const parsedSettings = JSON.parse(stored);
        setSettings({ ...DEFAULT_SETTINGS, ...parsedSettings });
      }
    } catch (error) {
      console.error("Failed to load analytics settings:", error);
      toast.error("Failed to load saved settings, using defaults");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateSettings = (newSettings: AnalyticsConfig) => {
    try {
      setSettings(newSettings);
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(newSettings));
    } catch (error) {
      console.error("Failed to save analytics settings:", error);
      toast.error("Failed to save settings");
    }
  };

  const resetSettings = () => {
    updateSettings(DEFAULT_SETTINGS);
    toast.success("Settings reset to defaults");
  };

  return {
    settings,
    updateSettings,
    resetSettings,
    isLoading
  };
};
